import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Image,
  Input,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateEmail,
  updatePassword,
  updateProfile,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../contexts/AuthContext";
import { functions } from "../../firebase/config";
import { recordActivity } from "../../utils/recordActivity";
import PageShell from "../layout/PageShell";
import ProfileSms from "./ProfileSms";

const ProfilePage: React.FC = () => {
  const toast = useToast();
  const { currentUser, forgotPassword } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [pendingSecret, setPendingSecret] = useState("");
  const [authenticatorCode, setAuthenticatorCode] = useState("");
  const [authenticatorEnabled, setAuthenticatorEnabled] = useState(false);
  const [authenticatorSaved, setAuthenticatorSaved] = useState(false);
  const [authenticatorConfiguredAt, setAuthenticatorConfiguredAt] = useState("");
  const [configuringAuthenticator, setConfiguringAuthenticator] = useState(false);

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textSecondary = useColorModeValue("gray.500", "gray.400");

  useEffect(() => {
    setDisplayName(currentUser?.displayName || "");
    setEmail(currentUser?.email || "");
    if (!currentUser) return;
    const status = httpsCallable<void, {
      isTwoFactorEnabled?: boolean;
      authenticatorSaved?: boolean;
      setUpAt?: string;
      qrCodeDataUrl?: string;
    }>(functions, "adminAccountStatus");
    status()
      .then((result) => {
        setAuthenticatorEnabled(result.data.isTwoFactorEnabled === true);
        setAuthenticatorSaved(result.data.authenticatorSaved === true);
        setAuthenticatorConfiguredAt(result.data.setUpAt || "");
        if (result.data.qrCodeDataUrl) setQrCodeDataUrl(result.data.qrCodeDataUrl);
      })
      .catch(() => undefined);
  }, [currentUser]);

  const handleSave = async () => {
    if (!currentUser) return;
    setSaving(true);
    try {
      const nextName = displayName.trim();
      if (nextName !== (currentUser.displayName || "")) {
        await updateProfile(currentUser, { displayName: nextName });
      }

      const nextEmail = email.trim();
      if (nextEmail && nextEmail !== currentUser.email) {
        await updateEmail(currentUser, nextEmail);
        await recordActivity("profile_update", true, "Display name or email updated");
        toast({
          title: "Profile saved",
          description: "Check your email to confirm the new address.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        await recordActivity("profile_update", true, "Display name updated");
        toast({
          title: "Profile saved",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      const needsLogin = error?.code === "auth/requires-recent-login";
      await recordActivity("profile_update", false, error.message || "Profile update failed");
      toast({
        title: needsLogin ? "Sign in again to change the email" : "Could not save profile",
        description: needsLogin
          ? "Log out, sign in, and try the email change again."
          : error.message || "Failed to update profile",
        status: needsLogin ? "warning" : "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentUser?.email) return;
    if (newPassword.length < 6) {
      toast({
        title: "Password is too short",
        description: "Use at least 6 characters.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords do not match",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setChangingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      await recordActivity("password_change", true, "Password changed");
      toast({
        title: "Password changed",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      const wrongPassword = error?.code === "auth/wrong-password" || error?.code === "auth/invalid-credential";
      await recordActivity("password_change", false, wrongPassword ? "Current password was incorrect" : (error.message || "Password change failed"));
      toast({
        title: wrongPassword ? "Current password is incorrect" : "Could not change password",
        description: wrongPassword ? undefined : error.message || "Try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleResetPassword = async () => {
    if (!currentUser?.email) return;
    setSendingReset(true);
    try {
      await forgotPassword(currentUser.email);
      await recordActivity("password_reset", true, "Password reset email sent");
      toast({
        title: "Reset email sent",
        description: `Check ${currentUser.email} for the reset link.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      await recordActivity("password_reset", false, error.message || "Reset email failed");
      toast({
        title: "Could not send reset email",
        description: error.message || "Try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingReset(false);
    }
  };

  const handleSetupAuthenticator = async () => {
    setSettingUp(true);
    try {
      const setupAdmin2fa = httpsCallable<void, { qrCodeDataUrl: string; secret: string }>(functions, "setupAdmin2fa");
      const result = await setupAdmin2fa();
      setQrCodeDataUrl(result.data.qrCodeDataUrl);
      setPendingSecret(result.data.secret);
      setAuthenticatorCode("");
      setAuthenticatorEnabled(false);
    } catch (error: any) {
      const alreadyEnabled = String(error?.message || "").includes("already enabled");
      if (alreadyEnabled) setAuthenticatorEnabled(true);
      toast({
        title: alreadyEnabled ? "Authenticator is already enabled" : "Could not start setup",
        description: alreadyEnabled ? undefined : error.message || "Try again.",
        status: alreadyEnabled ? "info" : "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSettingUp(false);
    }
  };

  const handleVerifyAuthenticator = async () => {
    setVerifying(true);
    try {
      const verifyAdmin2fa = httpsCallable<{ token: string }, { isTwoFactorEnabled: boolean }>(
        functions,
        "verifyAdmin2fa"
      );
      await verifyAdmin2fa({ token: authenticatorCode });
      setAuthenticatorEnabled(true);
      setAuthenticatorSaved(true);
      setConfiguringAuthenticator(false);
      toast({
        title: "Authenticator verification enabled",
        description: "Sign-in requires the 6-digit code from Nuage POS Super Admin.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Code was not accepted",
        description: error.message || "Enter the current code from your authenticator app.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleEnableAuthenticator = async () => {
    setSettingUp(true);
    try {
      const enable = httpsCallable(functions, "enableAdmin2fa");
      await enable();
      setAuthenticatorEnabled(true);
      setAuthenticatorSaved(true);
      toast({ title: "Authenticator verification enabled", status: "success", duration: 3000, isClosable: true });
    } catch (error: any) {
      toast({ title: "Could not enable authenticator", description: error.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setSettingUp(false);
    }
  };

  const handleDisableAuthenticator = async () => {
    setSettingUp(true);
    try {
      const disable = httpsCallable(functions, "disableAdmin2fa");
      await disable();
      setAuthenticatorEnabled(false);
      setAuthenticatorSaved(true);
      toast({
        title: "Authenticator verification disabled",
        description: "The Nuage POS Super Admin entry remains in Google Authenticator.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({ title: "Could not disable authenticator", description: error.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setSettingUp(false);
    }
  };

  return (
    <PageShell
      title="Profile"
      description="Your sign-in name, email, password, and authenticator."
      actions={
        <Button onClick={handleSave} isLoading={saving}>
          Save
        </Button>
      }
    >
      <VStack spacing={6} align="stretch">
        <Box bg={bgCard} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm" p={6}>
          <Text fontWeight="600" fontSize="md" mb={4}>Account</Text>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Display name</FormLabel>
              <Input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
              <Text fontSize="xs" color={textSecondary} mt={1}>
                Changing the email asks you to sign in again.
              </Text>
            </FormControl>
          </VStack>
        </Box>

        <Box bg={bgCard} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm" p={6}>
          <Text fontWeight="600" fontSize="md" mb={1}>Password</Text>
          <Text fontSize="sm" color={textSecondary} mb={4}>
            Change it here, or send a reset link to your email.
          </Text>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Current password</FormLabel>
              <Input
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
              />
            </FormControl>
            <FormControl>
              <FormLabel>New password</FormLabel>
              <Input
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Confirm new password</FormLabel>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
              />
            </FormControl>
            <Box>
              <Button
                onClick={handleChangePassword}
                isLoading={changingPassword}
                isDisabled={!currentPassword || !newPassword || !confirmPassword}
                mr={3}
              >
                Change password
              </Button>
              <Button variant="outline" onClick={handleResetPassword} isLoading={sendingReset}>
                Send reset email
              </Button>
            </Box>
          </VStack>
        </Box>

        <ProfileSms />

        <Box
          bg={authenticatorSaved ? "green.50" : bgCard}
          borderRadius="lg"
          border="1px"
          borderColor={authenticatorSaved ? "green.300" : borderColor}
          boxShadow="sm"
          p={6}
        >
          <Text fontWeight="600" fontSize="md" mb={1}>Authenticator</Text>
          <Text fontSize="sm" color={textSecondary} mb={3}>
            {authenticatorSaved
              ? "Google Authenticator is configured for this account under the name Nuage POS Super Admin. When authenticator verification is enabled, sign-in requires the 6-digit code from that application. When it is disabled, the code is not required. The authenticator entry remains on the device."
              : "Authenticator verification uses the Google Authenticator application. After the password, sign-in requires a 6-digit code from that application. An internet connection is required on the phone. Cellular service is not required."}
          </Text>
          <Text fontSize="sm" fontWeight="600" mb={authenticatorSaved && qrCodeDataUrl ? 3 : 4}>
            {authenticatorSaved ? "Configured" : "Not configured"}
            {authenticatorSaved ? ` · Sign-in requirement: ${authenticatorEnabled ? "Enabled" : "Disabled"}` : ""}
            {authenticatorConfiguredAt ? ` · Configured on ${new Date(authenticatorConfiguredAt).toLocaleString()}` : ""}
          </Text>
          {authenticatorSaved && qrCodeDataUrl && (
            <Image src={qrCodeDataUrl} alt="Nuage POS Super Admin authenticator code" boxSize="180px" bg="white" p={2} mb={4} />
          )}
          {authenticatorEnabled && (
            <Button variant="outline" onClick={handleDisableAuthenticator} isLoading={settingUp}>
              Disable
            </Button>
          )}
          {!authenticatorEnabled && authenticatorSaved && (
            <Button onClick={handleEnableAuthenticator} isLoading={settingUp}>
              Enable
            </Button>
          )}
          {!authenticatorSaved && !configuringAuthenticator && (
            <Button onClick={() => setConfiguringAuthenticator(true)}>
              Configure authenticator
            </Button>
          )}
          {!authenticatorSaved && configuringAuthenticator && (
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color={textSecondary}>
                Install Google Authenticator, open it, then select Add and scan the code. The account name must be Nuage POS Super Admin. Enter the current 6-digit code to complete configuration.
              </Text>
              <Button onClick={handleSetupAuthenticator} isLoading={settingUp} alignSelf="flex-start">
                Generate authenticator code
              </Button>
              {qrCodeDataUrl && (
                <>
                  <Image src={qrCodeDataUrl} alt="Nuage POS Super Admin authenticator code" boxSize="180px" bg="white" p={2} />
                  <FormControl>
                    <FormLabel>Manual entry key</FormLabel>
                    <Input value={pendingSecret} isReadOnly fontFamily="mono" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Verification code</FormLabel>
                    <Input
                      value={authenticatorCode}
                      onChange={(event) => setAuthenticatorCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                    />
                  </FormControl>
                  <Button
                    onClick={handleVerifyAuthenticator}
                    isLoading={verifying}
                    isDisabled={authenticatorCode.length !== 6}
                    alignSelf="flex-start"
                  >
                    Complete configuration
                  </Button>
                </>
              )}
              <Button variant="ghost" alignSelf="flex-start" onClick={() => setConfiguringAuthenticator(false)}>
                Cancel
              </Button>
            </VStack>
          )}
        </Box>
      </VStack>
    </PageShell>
  );
};

export default ProfilePage;
